import {
  EuiBadge,
  EuiButton,
  EuiCallOut,
  EuiContextMenuItem,
  EuiContextMenuPanel,
  EuiEmptyPrompt,
  EuiFlexGroup,
  EuiFlexItem,
  EuiForm,
  EuiFormRow,
  EuiHorizontalRule,
  EuiIcon,
  EuiLink,
  EuiPopover,
  EuiSelect,
  EuiSpacer,
  EuiStat,
  EuiTitle,
  EuiToolTip,
} from '@elastic/eui';
import type { MouseEvent } from 'react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import type { DetailedPortfolioAsset } from './portfolios_assets_table';
import { PortfoliosAssetsTable } from './portfolios_assets_table';
import type { DetailedPortfolioFund } from './portfolios_funds_table';
import { PortfoliosFundsTable } from './portfolios_funds_table';
import { ImportPortfolioModal } from './portfolios_import_portfolio_modal';
import { DeletePortfolioModal } from '../../components';
import { usePageMeta } from '../../hooks';
import type { Portfolio } from '../../model';
import {
  CSVReporter,
  Downloader,
  formatAum,
  formatPrice,
  formatTer,
  getDomicileURL,
  isFundAnalyzable,
  parseAssetSourceId,
  QUERY_PARAM_NAME,
  settingsDontShowDataNotice,
  settingsRemovePortfolio,
} from '../../model';
import { PageContext } from '../../page_container';

export function PortfoliosPage() {
  usePageMeta('Portfolios');

  const navigate = useNavigate();
  const { settings, setSettings, parameters, domicileCode } = useContext(PageContext);

  const { portfolio: portfolioNameParam } = useParams<{ portfolio?: string }>();
  const [currentPortfolio, setCurrentPortfolio] = useState<Portfolio | null>(() => {
    return (
      (portfolioNameParam ? settings.portfolios.find((portfolio) => portfolio.name === portfolioNameParam) : null) ??
      settings.portfolios.find((portfolio) => portfolio.assets.length > 0 || portfolio.funds.length > 0) ??
      null
    );
  });

  const [portfolioTotals, setPortfolioTotals] = useState<{
    fundsValue: number | null;
    fundsTer: number | null;
    assetsValue: number | null;
  }>({ fundsValue: null, fundsTer: null, assetsValue: null });
  const [selectedFunds, setSelectedFunds] = useState<DetailedPortfolioFund[]>([]);
  const [selectedAssets, setSelectedAssets] = useState<DetailedPortfolioAsset[]>([]);

  const onFundsValueChange = useCallback((value: number, ter: number) => {
    setPortfolioTotals((totals) => ({ ...totals, fundsValue: value, fundsTer: ter }));
  }, []);

  const onAssetsValueChange = useCallback((value: number) => {
    setPortfolioTotals((totals) => ({ ...totals, assetsValue: value }));
  }, []);

  useEffect(() => {
    if (currentPortfolio && (currentPortfolio.assets.length > 0 || currentPortfolio.funds.length > 0)) {
      return;
    }

    const portfolio = portfolioNameParam
      ? settings.portfolios.find((portfolio) => portfolio.name === portfolioNameParam)
      : undefined;

    setCurrentPortfolio(
      portfolio ??
        settings.portfolios.find((portfolio) => portfolio.assets.length > 0 || portfolio.funds.length > 0) ??
        null,
    );
  }, [portfolioNameParam, currentPortfolio, settings]);

  useEffect(() => {
    if (currentPortfolio) {
      setPortfolioTotals({ assetsValue: null, fundsValue: null, fundsTer: null });
      navigate(getDomicileURL(domicileCode, `/portfolios/${encodeURIComponent(currentPortfolio.name)}`), {
        replace: true,
      });
    } else {
      navigate(getDomicileURL(domicileCode, '/portfolios'), { replace: true });
    }
  }, [currentPortfolio, domicileCode, navigate]);

  const [isDeletePortfolioModalVisible, setIsDeletePortfolioModalVisible] = useState<boolean>(false);
  const toggleDeletePortfolioModal = useCallback(() => {
    setIsDeletePortfolioModalVisible(!isDeletePortfolioModalVisible);
  }, [isDeletePortfolioModalVisible]);

  const [isImportPortfolioModalVisible, setIsImportPortfolioModalVisible] = useState<boolean>(false);
  const toggleImportPortfolioModal = useCallback(() => {
    setIsImportPortfolioModalVisible(!isImportPortfolioModalVisible);
  }, [isImportPortfolioModalVisible]);

  const analyzableSelectionState = useMemo(() => {
    const nonAnalyzableFund = selectedFunds.find((fund) => !fund.profile || !isFundAnalyzable(fund.profile));
    const assetClassName =
      (nonAnalyzableFund?.profile?.details?.assetClass &&
        parameters.fundAssetClasses.get(nonAnalyzableFund?.profile?.details?.assetClass)?.name) ??
      nonAnalyzableFund?.profile?.details?.assetClass ??
      'Unknown';
    const { maxFundsToAnalyze, maxAssetsToAnalyze } = parameters.license;

    const fundsExceeded = selectedFunds.length > maxFundsToAnalyze;
    const assetsExceeded = selectedAssets.length > maxAssetsToAnalyze;

    const fundWithZeroValue = selectedFunds.find(
      (fund) => fund.units * (fund.profile?.details?.price ?? fund.price ?? 0) === 0,
    );
    const assetWithZeroValue = selectedAssets.find(
      (asset) => asset.units * (asset.profile?.details?.price ?? asset.price ?? 0) === 0,
    );
    if (fundWithZeroValue != null || assetWithZeroValue != null) {
      return {
        allComparable: false,
        message:
          'You can only analyze assets with non-zero value. Set non-zero price and units values for all selected assets and try again.',
      };
    }

    if (nonAnalyzableFund == null && !fundsExceeded && !assetsExceeded) {
      return { allComparable: true };
    }

    if (nonAnalyzableFund != null) {
      return { allComparable: false, message: `"${assetClassName}" funds cannot be analyzed.` };
    }

    return {
      allComparable: false,
      message:
        `You can analyze up to ${maxFundsToAnalyze} funds and ${maxAssetsToAnalyze} assets at once, ` +
        `but selected ${selectedFunds.length} funds and ${selectedAssets.length} assets.`,
    };
  }, [parameters, selectedFunds, selectedAssets]);

  const [isExportPopoverOpen, setIsExportPopoverOpen] = useState<boolean>(false);
  const onExportAsCSV = useCallback(() => {
    setIsExportPopoverOpen(false);

    const fundsRows: string[][] =
      selectedFunds.length > 0
        ? [
            ['Type', 'ID', 'Name', 'Asset Class', 'TER', 'AUM', 'Units', 'Link'],
            ...selectedFunds.map((fund) => [
              'ETF',
              fund.isin,
              fund.profile
                ? `${parameters.providers.get(fund.profile.provider)!.name} ${fund.profile.name.toUpperCase()}`
                : '-',
              fund.profile?.details
                ? (parameters.fundAssetClasses.get(fund.profile?.details.assetClass)?.name ??
                  fund.profile?.details.assetClass)
                : '-',
              fund.profile?.details ? formatTer(fund.profile?.details.ter) : '-',
              fund.profile?.details ? formatAum(fund.profile?.details.aum) : '-',
              fund.units.toString(),
              fund.profile?.details ? fund.profile?.details.url : '',
            ]),
          ]
        : [];
    const assetsRows: string[][] =
      selectedAssets.length > 0
        ? [
            ['Type', 'ID', 'Name', 'Sector', 'Country', 'Market Cap', 'Units', 'Link'],
            ...selectedAssets.map((asset) => [
              'Asset',
              asset.profile?.id ?? parseAssetSourceId(asset.id),
              asset.profile?.name.toUpperCase() ?? asset.name?.toUpperCase() ?? '-',
              asset.profile?.details?.sector ?? '-',
              asset.profile?.details?.country ?? '-',
              formatAum(asset.profile?.details?.marketCap ?? 0),
              asset.units.toString(),
              asset.profile?.details?.url ?? '',
            ]),
          ]
        : [];
    CSVReporter.download(`portfolio-${currentPortfolio?.name}.csv`, [
      ...fundsRows,
      ...(fundsRows.length > 0 && assetsRows.length > 0 ? [['', '', '', '', '', '', '', '']] : []),
      ...assetsRows,
    ]);
  }, [selectedFunds, selectedAssets, currentPortfolio, parameters]);

  const onExportAsJSON = useCallback(() => {
    setIsExportPopoverOpen(false);

    if (!currentPortfolio) {
      return;
    }

    Downloader.download(
      `portfolio-${currentPortfolio.name}.json`,
      JSON.stringify({
        name: currentPortfolio.name,
        funds:
          selectedFunds.length > 0
            ? selectedFunds.map((fund) => ({
                isin: fund.isin,
                name: fund.profile?.name.toUpperCase() ?? fund.name?.toUpperCase(),
                ter: fund.profile?.details?.ter,
                aum: fund.profile?.details?.aum,
                infoLink: fund.profile?.details?.url,
                units: fund.units,
                price: fund.price,
              }))
            : [],
        assets:
          selectedAssets.length > 0
            ? selectedAssets.map((asset) => ({
                id: asset.id,
                name: asset.profile?.name.toUpperCase() ?? asset.name ?? '-',
                infoLink: asset.profile?.details?.url,
                units: asset.units,
                price: asset.price,
              }))
            : [],
      }),
      'data:text/json;charset=utf-8',
    );
  }, [selectedFunds, selectedAssets, currentPortfolio]);

  const onAnalyze = useCallback(() => {
    navigate(
      getDomicileURL(
        domicileCode,
        `/analyze?${QUERY_PARAM_NAME}=${encodeURIComponent(
          btoa(
            JSON.stringify({
              funds: selectedFunds.map((fund) => [
                fund.isin,
                fund.units * (fund.profile?.details?.price ?? fund.price ?? 0),
              ]),
              assets: selectedAssets.map((asset) => [
                asset.id,
                asset.units * (asset.profile?.details?.price ?? asset.price ?? 0),
              ]),
            }),
          ),
        )}`,
      ),
    );
  }, [selectedFunds, selectedAssets, domicileCode, navigate]);

  const importPortfolioButton = (
    <EuiButton
      iconType={'importAction'}
      onClick={toggleImportPortfolioModal}
      title="Import previously exported portfolio"
    >
      Import
    </EuiButton>
  );

  const importModal = isImportPortfolioModalVisible ? (
    <ImportPortfolioModal
      onCancel={toggleImportPortfolioModal}
      onImport={(portfolio) => {
        setIsImportPortfolioModalVisible(false);
        setCurrentPortfolio(portfolio);
      }}
    />
  ) : null;

  if (!currentPortfolio) {
    return (
      <EuiFlexGroup
        direction={'column'}
        gutterSize={'s'}
        justifyContent="center"
        alignItems="center"
        style={{ height: '100%' }}
      >
        <EuiFlexItem>
          <EuiEmptyPrompt
            icon={<EuiIcon type={'submodule'} size={'xl'} />}
            title={<h2>You don&apos;t have any portfolios yet</h2>}
            titleSize="s"
            style={{ maxWidth: '60em', display: 'flex' }}
            body={
              <div>
                <p>
                  Go to the{' '}
                  <EuiLink
                    href={getDomicileURL(domicileCode, '/')}
                    onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                      e.preventDefault();

                      navigate(getDomicileURL(domicileCode, '/'));
                    }}
                  >
                    Funds page
                  </EuiLink>{' '}
                  and save some funds to a portfolio.
                </p>
                {importPortfolioButton}
              </div>
            }
          />
        </EuiFlexItem>
        {importModal}
      </EuiFlexGroup>
    );
  }

  const selectionAnalyzable =
    selectedFunds.length + selectedAssets.length >= 2 && analyzableSelectionState.allComparable;

  const dataNotice =
    settings.dontShowDataNotice !== true ? (
      <EuiFlexItem grow={false}>
        <EuiCallOut title="Where is your data stored?" color="warning" iconType="help">
          <p>
            Your portfolios and personal settings are stored locally in the cache of your browser and not synced between
            your devices. No one except you can access your data, and it remains available as long as you don&apos;t
            clear the cache. For more information, refer to{' '}
            <EuiLink
              href={getDomicileURL(domicileCode, '/privacy')}
              onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                e.preventDefault();
                navigate(getDomicileURL(domicileCode, '/privacy'));
              }}
            >
              Privacy Policy
            </EuiLink>
            .
          </p>
          <p>
            You can backup and transfer your portfolios at any time by using the{' '}
            <EuiBadge color="hollow" iconType="exportAction" iconSide="left">
              Export
            </EuiBadge>{' '}
            functionality.
          </p>
          <EuiButton
            color="warning"
            onClick={() => {
              setSettings(settingsDontShowDataNotice(settings));
            }}
          >
            Understood
          </EuiButton>
        </EuiCallOut>
        <EuiSpacer />
      </EuiFlexItem>
    ) : null;

  const fundsTable =
    currentPortfolio && currentPortfolio.funds.length > 0 ? (
      <EuiFlexItem grow={false}>
        <EuiSpacer />
        <EuiTitle>
          <h3>ETFs</h3>
        </EuiTitle>
        <EuiSpacer />
        <PortfoliosFundsTable
          portfolio={currentPortfolio}
          onSelectionChange={setSelectedFunds}
          onValueChange={onFundsValueChange}
        />
      </EuiFlexItem>
    ) : null;

  const assetsTable =
    currentPortfolio && currentPortfolio.assets.length > 0 ? (
      <EuiFlexItem grow={false}>
        {currentPortfolio.funds.length > 0 ? <EuiHorizontalRule size="half" /> : <EuiSpacer />}
        <EuiTitle>
          <h3>Stocks, Bonds, and Other Securities</h3>
        </EuiTitle>
        <EuiSpacer />
        <PortfoliosAssetsTable
          portfolio={currentPortfolio}
          onSelectionChange={setSelectedAssets}
          onValueChange={onAssetsValueChange}
        />
      </EuiFlexItem>
    ) : null;

  const deleteModal = isDeletePortfolioModalVisible ? (
    <DeletePortfolioModal
      portfolioName={currentPortfolio.name}
      onCancel={toggleDeletePortfolioModal}
      onConfirm={() => {
        if (currentPortfolio) {
          setSettings(settingsRemovePortfolio(settings, currentPortfolio.name));
          setCurrentPortfolio(null);
        }

        setIsDeletePortfolioModalVisible(false);
      }}
    />
  ) : null;

  const areTotalsLoading =
    !currentPortfolio ||
    (currentPortfolio.assets.length > 0 && portfolioTotals.assetsValue === null) ||
    (currentPortfolio.funds.length > 0 && portfolioTotals.fundsValue === null);

  const totalPortfolioTer =
    currentPortfolio.funds.length > 0 ? (
      <EuiFlexItem>
        <EuiStat
          reverse
          title={formatTer(portfolioTotals.fundsTer ?? 0)}
          isLoading={areTotalsLoading}
          description={
            <EuiToolTip content="Total Expense Ratio (TER) of the portfolio is the weighted average of all ETF TERs">
              <span>
                Total Expense Ratio (TER)
                <EuiIcon size="s" color="subdued" type="questionInCircle" className="eui-alignTop" />
              </span>
            </EuiToolTip>
          }
          titleSize="m"
          textAlign="center"
        />
      </EuiFlexItem>
    ) : null;

  return (
    <>
      <EuiFlexGroup direction={'column'} gutterSize={'s'} style={{ height: '100%' }}>
        {dataNotice}
        <EuiFlexItem grow={false}>
          <EuiForm>
            <EuiFormRow fullWidth>
              <EuiFlexGroup direction={'row'} alignItems="center" justifyContent={'spaceBetween'} gutterSize={'s'}>
                <EuiFlexItem>
                  <EuiSelect
                    prepend={'Portfolio'}
                    options={settings.portfolios.map(({ name }) => ({ value: name, text: name }))}
                    value={currentPortfolio.name}
                    onChange={(e) => {
                      setCurrentPortfolio(
                        settings.portfolios.find((portfolio) => portfolio.name === e.target.value) ?? null,
                      );
                    }}
                  />
                </EuiFlexItem>
                <EuiFlexItem grow={false}>{importPortfolioButton}</EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <EuiPopover
                    className="eui-fullWidth"
                    button={
                      <EuiButton
                        fullWidth={true}
                        disabled={selectedFunds.length === 0 && selectedAssets.length === 0}
                        iconType={'exportAction'}
                        onClick={() => setIsExportPopoverOpen(!isExportPopoverOpen)}
                        title={
                          selectedFunds.length > 0 || selectedAssets.length > 0
                            ? 'Export selected funds and assets'
                            : 'Select at least one fund or asset to use export'
                        }
                      >
                        Export
                      </EuiButton>
                    }
                    isOpen={isExportPopoverOpen}
                    closePopover={() => setIsExportPopoverOpen(false)}
                    panelPaddingSize="none"
                    anchorPosition="downLeft"
                  >
                    <EuiContextMenuPanel
                      size="m"
                      items={[
                        <EuiContextMenuItem key="csv" icon="visTable" onClick={onExportAsCSV}>
                          CSV
                        </EuiContextMenuItem>,
                        <EuiContextMenuItem key="json" icon="nested" onClick={onExportAsJSON}>
                          JSON
                        </EuiContextMenuItem>,
                      ]}
                    />
                  </EuiPopover>
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <EuiButton
                    disabled={!selectionAnalyzable}
                    iconType={analyzableSelectionState.allComparable ? 'inputOutput' : 'alert'}
                    onClick={onAnalyze}
                    title={
                      selectionAnalyzable
                        ? 'Analyze selected funds and assets'
                        : (analyzableSelectionState.message ?? 'Select at least two funds or assets to analyze')
                    }
                  >
                    Analyze
                  </EuiButton>
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <EuiButton
                    color="danger"
                    iconType="trash"
                    onClick={toggleDeletePortfolioModal}
                    title="Delete portfolio"
                  >
                    Delete
                  </EuiButton>
                </EuiFlexItem>
              </EuiFlexGroup>
            </EuiFormRow>
          </EuiForm>
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiSpacer />
          <EuiFlexGroup>
            <EuiFlexItem>
              <EuiStat
                reverse
                title={formatPrice((portfolioTotals.assetsValue ?? 0) + (portfolioTotals.fundsValue ?? 0))}
                description="Total Portfolio Value (TPV)"
                isLoading={areTotalsLoading}
                titleSize="m"
                textAlign="center"
              />
            </EuiFlexItem>
            <EuiFlexItem>
              <EuiStat
                reverse
                title={currentPortfolio ? currentPortfolio.funds.length + currentPortfolio.assets.length : '-'}
                description="Total Portfolio Positions"
                isLoading={!currentPortfolio}
                titleSize="m"
                textAlign="center"
              />
            </EuiFlexItem>
            {totalPortfolioTer}
          </EuiFlexGroup>
        </EuiFlexItem>
        {fundsTable}
        {assetsTable}
      </EuiFlexGroup>
      {deleteModal}
      {importModal}
    </>
  );
}
